import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../seo/useSeo';
import SEO_PAGES from '../seo/pages.json';

const SHOW_PHOTOBOOTH = process.env.REACT_APP_SHOW_PHOTOBOOTH === 'true';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  useSeo({ ...SEO_PAGES['/faq'], canonicalPath: '/faq' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFAQ = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Store current scroll position
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    setActiveIndex(activeIndex === index ? null : index);
    
    // Prevent scroll jump by restoring position after state update
    // Use multiple frames to ensure it sticks
    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    });
  };

  useEffect(() => {
    // Scroll animations for FAQ page
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    const fadeElements = document.querySelectorAll('.fade-in');
    fadeElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const faqData = [
    {
      question: "What areas do you serve?",
      answer: "We serve the greater Kansas City metropolitan area, covering both the Missouri and Kansas sides of the metro. If you're unsure whether we cover your area, reach out — we're happy to discuss options."
    },
    {
      question: "How are marquee letters priced?",
      answer: "Marquee letters are priced at $25 per letter for the Foam Glow option. Delivery, setup, and collection is $35, waived free on bookings of $100 or more. Book both a photo booth and marquee letters together and save 10% automatically."
    },
    {
      question: "How is the photo booth priced?",
      answer: "The photo booth starts at $450 for 3 hours (Unlimited Digital Photos) or $650 for 3 hours (Unlimited Prints + Digital Photos), with a 3-hour minimum rental. Additional hours are $75/hr or $100/hr respectively. Our handmade wooden booth features a touchscreen interface and a professional DSLR camera for stunning, studio-quality photos."
    },
    {
      question: "Do guests get photos from the photo booth?",
      answer: "Yes — every guest receives high-quality DSLR photos with both packages. The Unlimited Digital Photos package delivers instant digital access to save and share from the event. The Unlimited Prints package includes everything in digital, plus physical prints on the spot."
    },
    {
      question: "How long does setup and teardown take?",
      answer: "Setup and teardown typically take about 30 minutes. We arrive ahead of your event start time to ensure everything is ready."
    },
    {
      question: "Is there a delivery and setup fee?",
      answer: "Delivery, setup, and collection is $35 for marquee letter bookings. This fee is waived for free on bookings of $100 or more — so 4 or more letters and delivery is on us."
    },
    {
      question: "What if there's bad weather for outdoor events?",
      answer: "We require a covered area or indoor backup space for outdoor events. Please ensure your venue has a suitable covered area available."
    },
    {
      question: "What's your cancellation and payment policy?",
      answer: "We require a 50% deposit to secure your date. Full refunds are available up to 60 days before your event. Final pricing is confirmed upon booking."
    }
  ];

  return (
    <div className="faq-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map(({ question, answer }) => ({
              '@type': 'Question',
              name: question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: answer
              }
            }))
          })
        }}
      />
      <div className="faq-hero">
        <div className="section-container">
          <h1 className="serif">Frequently Asked Questions</h1>
          <p>Everything you need to know about our {SHOW_PHOTOBOOTH ? 'photo booth and marquee letter' : 'marquee letter'} services</p>
        </div>
      </div>
      <div className="section-container">
        <div className="faq-content">
          {faqData.map((item, index) => (
            <div key={index} className={`faq-item fade-in ${activeIndex === index ? 'active' : ''}`}>
              <div className="faq-question" onClick={(e) => toggleFAQ(index, e)}>
                <span>{item.question}</span>
              </div>
              <div className="faq-answer">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="faq-contact fade-in">
        <div className="section-container">
          <h3 className="serif">Still have questions?</h3>
          <p>We're here to help! Contact us for personalized assistance.</p>
          <Link to="/contact" className="contact-btn">Get in Touch</Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
